<template>
  <div>
    <v-row>
      <v-col>
        <v-toolbar flat>
          <v-toolbar-title class="capitalize">
            <div>
              {{ $tc('common.delivery') }} - {{ $tc('common.status', 2, { n: '' }) }}
            </div>
          </v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            solo
            hide-details
            dense
            clearable
            class="mr-3"
            append-icon="mdi-magnify"
          />
          <v-spacer />
          <v-menu
            v-model="addMenu"
            left
            :close-on-content-click="false"
            min-width="500"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('common.new') }} {{ $tc('common.status', 1, { n: '' }) }}
                <v-icon right> mdi-plus </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                {{ $t('common.new') }} {{ $tc('common.status', 1, { n: '' }) }}
              </v-card-title>
              <v-card-text>
                <v-form
                  ref="form"
                  v-model="addForm"
                >
                  <v-text-field
                    ref="newItemTitle"
                    v-model="newItem.title"
                    label="Navn"
                    autofocus
                    outlined
                    :rules="[ v => !!v || 'Udfyld et navn' ]"
                  />
                  <v-sheet
                    class="pa-3 mb-4"
                    outlined
                    rounded
                  >
                    <div class="subtitle">
                      Vælg en farve
                    </div>
                    <v-color-picker
                      v-model="colorInput"
                      :rules="[ v => !!v || 'Udfyld en farve' ]"
                      show-swatches
                      class="mt-2"
                    />
                  </v-sheet>
                  <v-text-field
                    label="Sortering"
                    type="number"
                    v-model="newItem.sortOrder"
                    clearable
                    outlined
                  />
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  @click="clear(false); addMenu = false;"
                  small
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-btn text small @click="clear(true)">
                  Ryd
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  :disabled="!addForm"
                  :loading="creating"
                  @click="create"
                  small
                >
                  Opret
                  <v-icon right> mdi-plus </v-icon>
                </v-btn>
                <v-btn
                  :disabled="!addForm"
                  :loading="creating"
                  @click="create(true)"
                  small
                >
                  Opret og ny
                  <v-icon right> mdi-plus </v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-toolbar>
        <v-data-table
          :items="items"
          :headers="headers"
          :loading="loading"
          :search="search"
          :items-per-page="15"
          sort-by="sortOrder"
          :footer-props="{ 'items-per-page-options': [15, 30, 50, 75, -1] }"
        >
          <template #[`item.hexColor`]="{ value }">
            <v-chip label :color="value">
              <tt>{{ value ?? 'Not set' }}</tt>
            </v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn small @click="onEditItem(item)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editForm"
          >
            <v-text-field
              ref="newItemTitle"
              v-model="editItem.title"
              label="Navn"
              autofocus
              outlined
              :rules="[ v => !!v || 'Udfyld et navn' ]"
            />
            <v-sheet
              class="pa-3 mb-4"
              outlined
              rounded
            >
              <div class="subtitle">
                Vælg en farve
              </div>
              <v-color-picker
                v-model="colorInput"
                :rules="[ v => !!v || 'Udfyld en farve' ]"
                show-swatches
                class="mt-2"
              />
            </v-sheet>
            <v-text-field
              label="Sortering"
              type="number"
              v-model="editItem.sortOrder"
              clearable
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-menu
            v-model="deleteMenu"
            width="500"
            :close-on-content-click="false"
          >
            <template #activator="{ attrs, on }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                color="error"
                text
              >
                Slet
                <v-icon right>
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                Bekræft sletning
              </v-card-title>
              <v-card-text>
                Er du sikker på du vil slette?
              </v-card-text>
              <v-card-actions>
                <v-btn text @click="deleteMenu = false">
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn
                  :loading="deleting"
                  @click="deleteItem(editItem.id)"
                >
                  Ja, slet
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-spacer />

          <v-btn :loading="saving" @click="saveItem()">
            {{ $t('common.save') }}
            <v-icon right> mdi-content-save-outline </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const baseUrl = 'delivery-statuses';

export default {
  name: 'CustomerStatuses',
  props: ['customer'],
  data() {
    return {
      loading: true,
      creating: false,
      saving: false,
      deleting: false,
      addMenu: false,
      deleteMenu: false,
      editDialog: false,
      editItem: {},
      addForm: false,
      showColorPicker: false,
      colorInput: null,
      editForm: false,
      newItem: {},
      items: [],
      headers: [
        { text: 'Navn', value: 'title', width: '30%' },
        { text: 'Sortering', value: 'sortOrder', align: 'right' },
        { text: 'Farve', value: 'hexColor', align: 'right', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false }
      ],
      search: '',
    }
  },
  mounted: async function() {
    this.getItems();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = {};
        this.colorInput = null;
      }
    },
    addMenu(value) {
      if (value) {
        this.colorInput = '#cccccc';
      }
    }
  },
  methods: {
    create: async function(leaveOpen = false) {
      this.creating = true;

      try {
        const model = {
          ...this.newItem,
          hexColor: this.colorInput,
          customerId: this.$route.params.customerId
        };

        const { data } = await this.$api.post(baseUrl, model);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        if (!leaveOpen || typeof leaveOpen !== 'boolean') {
          this.addMenu = false;
          this.clear(true);
        } else {
          this.clear(false);
          this.$refs.newItemTitle.focus();
        }

        this.$notifier.showSuccess({ message: `${this.$tc('common.status', 1, { n: '' })} oprettet` });
        this.getItems();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.creating = false;
      }
    },
    onEditItem: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.colorInput = this.editItem.hexColor;
      this.editDialog = true;
    },
    saveItem: async function() {
      this.saving = true;

      try {
        this.editItem.hexColor = this.colorInput;
        const { data } = await this.$api.patch(`${baseUrl}/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: `${this.$tc('common.status', 1, { n: '' })} opdateret` });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    deleteItem: async function(id) {
      this.deleting = true;

      try {
        const { data } = await this.$api.delete(`${baseUrl}/${id}`);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: `${this.$tc('common.status', 1, { n: '' })} slettet` });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.deleting = false;
      }
    },
    clear: function() {
      this.newItem = {};
      this.$refs.form.resetValidation();
    },
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get(baseUrl, {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>